/**
 * Camera collision (pure, sim yards, no three.js): a 2.5D list of vertical blockers (wall faces,
 * posts, big authored props) built once per dungeon from the arch layout, and a segment cast
 * from the camera pivot to the ideal eye. A blocker is hit where the cast crosses its footprint
 * edge below its top. Segments live in typed arrays bucketed in a uniform grid; a cast visits
 * only the cells under the cast's bounding box and allocates nothing.
 *
 * Walls here are the dressing's (arch.ts WallPiece: inner face + top height), so far walls are
 * tall and near walls low, like what's drawn; the default high camera clears the low ones.
 *
 * Registering geometry (any dungeon builder): return `Blocker`s from `DungeonBuild.cameraBlockers()`
 * (dungeon.ts). A blocker is a vertical quad: footprint edge a→b (sim yards) spanning heights
 * base..top (absolute). Solid volumes = their footprint edges; an arch / lintel over a doorway =
 * a blocker with `base` at the opening's top. Ceilings or upper walls that a camera cutaway hides
 * must NOT be registered (the cast only knows vertical quads; there is no ceiling test).
 */
import type { Polygon, Vec2 } from '@keystone/sim';
import type { ArchLayout } from './dressing/arch';
import { computeWallLayout } from './layout';

const CELL = 6;
/** Floats per segment. */
const S = 6;
/** Wall tops count a bit higher so the near plane doesn't graze them. */
const TOP_MARGIN = 0.25;

export interface Blocker {
  a: Vec2;
  b: Vec2;
  /** Top height (yards, absolute). */
  top: number;
  /** Bottom height (yards, absolute); default: unbounded below (a wall standing on the floor). */
  base?: number;
}

export class CamCollision {
  /** ax, ay, bx, by, top, base per segment. */
  private readonly seg: Float32Array;
  private readonly count: number;
  private readonly x0: number;
  private readonly y0: number;
  private readonly cols: number;
  private readonly rows: number;
  /** CSR grid: cellStart[c]..cellStart[c+1] index into cellItems. */
  private readonly cellStart: Int32Array;
  private readonly cellItems: Int32Array;
  private readonly stamp: Uint32Array;
  private frame = 0;

  constructor(blockers: readonly Blocker[]) {
    this.count = blockers.length;
    this.seg = new Float32Array(this.count * S);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    blockers.forEach((b, i) => {
      this.seg.set([b.a.x, b.a.y, b.b.x, b.b.y, b.top + TOP_MARGIN, b.base ?? -1e9], i * S);
      minX = Math.min(minX, b.a.x, b.b.x);
      minY = Math.min(minY, b.a.y, b.b.y);
      maxX = Math.max(maxX, b.a.x, b.b.x);
      maxY = Math.max(maxY, b.a.y, b.b.y);
    });
    if (!this.count) minX = minY = maxX = maxY = 0;
    this.x0 = minX - CELL;
    this.y0 = minY - CELL;
    this.cols = Math.max(1, Math.ceil((maxX - this.x0 + CELL) / CELL));
    this.rows = Math.max(1, Math.ceil((maxY - this.y0 + CELL) / CELL));
    const buckets: number[][] = Array.from({ length: this.cols * this.rows }, () => []);
    for (let i = 0; i < this.count; i++) {
      const s = i * S;
      const c0 = this.col(Math.min(this.seg[s]!, this.seg[s + 2]!));
      const c1 = this.col(Math.max(this.seg[s]!, this.seg[s + 2]!));
      const r0 = this.row(Math.min(this.seg[s + 1]!, this.seg[s + 3]!));
      const r1 = this.row(Math.max(this.seg[s + 1]!, this.seg[s + 3]!));
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) buckets[r * this.cols + c]!.push(i);
    }
    this.cellStart = new Int32Array(buckets.length + 1);
    let n = 0;
    buckets.forEach((b, i) => {
      this.cellStart[i] = n;
      n += b.length;
    });
    this.cellStart[buckets.length] = n;
    this.cellItems = new Int32Array(n);
    buckets.forEach((b, i) => this.cellItems.set(b, this.cellStart[i]!));
    this.stamp = new Uint32Array(this.count);
  }

  get size(): number {
    return this.count;
  }

  private col(x: number): number {
    return Math.min(this.cols - 1, Math.max(0, Math.floor((x - this.x0) / CELL)));
  }
  private row(y: number): number {
    return Math.min(this.rows - 1, Math.max(0, Math.floor((y - this.y0) / CELL)));
  }

  /**
   * First hit along P→E as a fraction 0..1 of the way (1 = clear). P/E: sim x, y and absolute
   * height h. Only blockers whose top is above the cast's height at the crossing count.
   */
  cast(px: number, py: number, ph: number, ex: number, ey: number, eh: number): number {
    if (!this.count) return 1;
    if (++this.frame === 0xffffffff) {
      this.stamp.fill(0);
      this.frame = 1;
    }
    const dx = ex - px;
    const dy = ey - py;
    const c0 = this.col(Math.min(px, ex));
    const c1 = this.col(Math.max(px, ex));
    const r0 = this.row(Math.min(py, ey));
    const r1 = this.row(Math.max(py, ey));
    let best = 1;
    const seg = this.seg;
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const cell = r * this.cols + c;
        for (let k = this.cellStart[cell]!, end = this.cellStart[cell + 1]!; k < end; k++) {
          const i = this.cellItems[k]!;
          if (this.stamp[i] === this.frame) continue;
          this.stamp[i] = this.frame;
          const s = i * S;
          const ax = seg[s]!;
          const ay = seg[s + 1]!;
          const sx = seg[s + 2]! - ax;
          const sy = seg[s + 3]! - ay;
          const den = dx * sy - dy * sx;
          if (Math.abs(den) < 1e-9) continue;
          const qx = ax - px;
          const qy = ay - py;
          const t = (qx * sy - qy * sx) / den;
          if (t < 0 || t >= best) continue;
          const u = (qx * dy - qy * dx) / den;
          if (u < 0 || u > 1) continue;
          const h = ph + (eh - ph) * t;
          if (h >= seg[s + 4]! || h < seg[s + 5]!) continue;
          best = t;
        }
      }
    }
    return best;
  }
}

/** Blockers from the themed arch layout: wall faces, posts and tall authored props. */
export function blockersFromArch(arch: ArchLayout): Blocker[] {
  const out: Blocker[] = [];
  for (const w of arch.walls) out.push({ a: w.a, b: w.b, top: w.top });
  for (const p of arch.posts) pushPoly(out, square(p.pos, p.size / 2), p.top);
  for (const h of arch.hints) {
    if (h.spec.floorDecal) continue;
    const top = h.y + h.spec.h * h.scale;
    if (top - h.y < 1.2 || h.foot.length < 3) continue; // low props never block the view
    pushPoly(out, h.foot, top);
  }
  return out;
}

/** Legacy (flat KayKit) dressing: walls along the walkable boundary, `height` tall. */
export function blockersFromWalkable(walkable: readonly Polygon[], height = 2.8): Blocker[] {
  return computeWallLayout(walkable).walls.map((w) => ({ a: w.a, b: w.b, top: height }));
}


function square(c: Vec2, h: number): Vec2[] {
  return [
    { x: c.x - h, y: c.y - h },
    { x: c.x + h, y: c.y - h },
    { x: c.x + h, y: c.y + h },
    { x: c.x - h, y: c.y + h },
  ];
}

function pushPoly(out: Blocker[], pts: readonly Vec2[], top: number): void {
  for (let i = 0; i < pts.length; i++) out.push({ a: pts[i]!, b: pts[(i + 1) % pts.length]!, top });
}

/**
 * +atmos: the WoW camera slide. Marches the boom from the pivot P (sim x, y, absolute height h)
 * along the unit direction U out to `reach` and returns the longest boom whose eye stays
 * `clearance` above the terrain at every sample (Infinity-free: `reach` when nothing is hit). A low
 * pitch then slides the eye in along the ground instead of sinking into it or blocking the pitch.
 */
export function boomGroundLimit(groundY: (x: number, y: number) => number, px: number, py: number, ph: number, ux: number, uy: number, uh: number, reach: number, clearance = 0.5, steps = 14): number {
  const under = (t: number): boolean => ph + uh * t < groundY(px + ux * t, py + uy * t) + clearance;
  let prev = 0;
  for (let i = 1; i <= steps; i++) {
    const t = (reach * i) / steps;
    if (under(t)) {
      // Refine between the last clear sample and this one.
      let lo = prev;
      let hi = t;
      for (let k = 0; k < 6; k++) {
        const m = (lo + hi) / 2;
        if (under(m)) hi = m;
        else lo = m;
      }
      return lo;
    }
    prev = t;
  }
  return reach;
}
